import { Pie, PieSvgProps, DefaultRawDatum } from '@nivo/pie';
import { darken, lighten, useTheme } from '@mui/material';
import { useEffect, useMemo, useRef, useState } from 'react';
import { NivoGridCard } from '@components/Charts/common/NivoGridContainer';
import AutoSizer from 'react-virtualized-auto-sizer';
import { ChartTitleProps } from '../common/ChartTitle/ChartTitle';
import ChartLegends from './Legend/PieChartLegends';
import Flex from '@components/Flex/Flex';
import { useMediaQueries } from '@utils/mediaQueries/mediaQueries';

export type PieChartProps = Omit<
  PieSvgProps<DefaultRawDatum>,
  'height' | 'width'
> &
  ChartTitleProps & {
    cardTooltipText?: string;
  };

const PieChart: React.FC<PieChartProps> = ({
  data,
  title,
  subtitle,
  cardTooltipText,
  ...restProps
}: PieChartProps) => {
  const theme = useTheme();
  const { sm: isMobile } = useMediaQueries();

  const containerRef = useRef<HTMLElement>();
  const [containerWidth, setContainerWidth] = useState(0);

  useEffect(() => {
    if (containerRef.current?.clientWidth) {
      setContainerWidth(containerRef.current?.clientWidth);
    }
  }, [containerRef.current?.clientWidth]);

  const { sortedData, sortedColors } = useMemo(() => {
    const sortedData = data.sort((x, y) => y.value - x.value);
    const sortedColors = sortedData.map((_x, index) =>
      lighten(theme.palette.primary.main, (1 / data.length) * index)
    );
    sortedColors.unshift(darken(theme.palette.primary.main, 0.6));
    sortedColors.pop();

    return { sortedData, sortedColors };
  }, [data, theme.palette.primary.main]);

  const CenteredLayer = ({
    centerX,
    centerY,
    innerRadius,
    ...rest
  }: {
    centerX: number;
    centerY: number;
    innerRadius: number;
  }) => {
    return (
      <>
        <circle
          cx={centerX}
          cy={centerY}
          r={innerRadius - 5}
          stroke='#F8F8F8'
          stroke-width='10'
          fill='#fff'
        />
        <text
          x={0}
          y={centerY}
          textAnchor='middle'
          dominantBaseline='central'
          style={{
            fontSize: '14px',
            fontWeight: 600,
          }}
        >
          {title.split(' ').map((x, i) => (
            <tspan x={centerX} dy={`${i === 0 ? -0.6 : 1.2 * i}em`}>
              {x}
            </tspan>
          ))}
        </text>
      </>
    );
  };

  return (
    <NivoGridCard
      ref={containerRef}
      sx={{
        height: isMobile ? 350 : 'auto',
        width: isMobile ? '100%' : 'auto',
      }}
    >
      <Flex
        flex={1}
        flexWrap='nowrap'
        sx={{
          flexDirection: {
            xs: 'column',
            sm: containerWidth > 350 ? 'row' : 'column',
          },
        }}
      >
        <Flex flex='1 0 auto'>
          <AutoSizer>
            {({ height, width }) => (
              <Pie
                data={sortedData}
                margin={{
                  top: isMobile ? 5 : 10,
                  bottom: isMobile ? 5 : 10,
                  left: 8,
                  right: 8,
                }}
                activeOuterRadiusOffset={8}
                colors={[...sortedColors]}
                innerRadius={0.75}
                startAngle={0}
                padAngle={1.5}
                cornerRadius={3}
                enableArcLabels={false}
                enableArcLinkLabels={false}
                sortByValue
                {...restProps}
                height={height}
                width={width}
                layers={['arcs', 'arcLabels', 'arcLinkLabels', CenteredLayer]}
              />
            )}
          </AutoSizer>
        </Flex>
        <Flex
          flex={isMobile ? 0 : 1}
          alignItems='center'
          marginLeft={12}
          marginY={isMobile ? 10 : 0}
        >
          <ChartLegends
            title=''
            data={data}
            colors={sortedColors}
            itemsContainerSx={{
              alignItems: 'flex-start',
              justifyContent: 'center',
              flex: 1,
              flexFlow: isMobile ? 'row wrap' : 'column nowrap',
              maxWidth: '100%',
            }}
          />
        </Flex>
      </Flex>
    </NivoGridCard>
  );
};

export default PieChart;
